import { ConfigProvider, Empty, theme as antdTheme } from 'antd5';
import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { type Layout, type Layouts, Responsive } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { PageHeaderLayout } from 'components/PageHeaderLayout';
import { Button, Spinner } from 'components/ui';
import { Theme } from 'utils/color/horizonTheme';
import { useFeatures } from 'utils/features';

import { DashboardGrid } from './CustomizableDashboard.style';
import { DashboardSkeleton } from './DashboardSkeleton';
import { DashboardToolbar } from './DashboardToolbar';
import {
  type WidgetAvailability,
  getAvailableWidgetIds,
  getWidgetAvailability,
} from './featureGating';
import type { WidgetId } from './types';
import { useDashboardLayout } from './useDashboardLayout';
import { useDragAutoScroll } from './useDragAutoScroll';
import { WidgetCatalogDrawer } from './WidgetCatalogDrawer';
import { WidgetFrame } from './WidgetFrame';
import { ALL_WIDGET_IDS, WIDGET_REGISTRY } from './widgetRegistry';
import {
  DASHBOARD_COLUMNS,
  DASHBOARD_GRID_MARGIN,
  DASHBOARD_ROW_HEIGHT,
  getWidgetSizePresetId,
} from './widgetSizePresets';

// A single breakpoint = a fixed 12-column grid at every width, so widgets can
// always be placed next to each other (responsive breakpoints were collapsing
// the grid to fewer columns and forcing everything into one stacked column).
const BREAKPOINTS = { lg: 0 };
const COLS = { lg: DASHBOARD_COLUMNS };
const ROW_HEIGHT = DASHBOARD_ROW_HEIGHT;
const MARGIN = DASHBOARD_GRID_MARGIN;

/**
 * The customizable per-user dashboard. View mode looks like a normal dashboard;
 * Edit mode unlocks drag (via the widget header handle), resize, remove, and the
 * Add-widget catalog. Layout lives in `useDashboardLayout`; persistence and
 * role-derived presets layer on in later phases.
 */
export function CustomizableDashboard() {
  const {
    layout,
    isLoaded,
    isEditing,
    setIsEditing,
    addWidget,
    removeWidget,
    onLayoutChange,
    onInteractionStart,
    onInteractionStop,
    setWidgetSize,
    updateWidgetConfig,
    saveStatus,
  } = useDashboardLayout();
  const { t } = useTranslation();
  const [catalogOpen, setCatalogOpen] = useState(false);
  const { updateAutoScroll, stopAutoScroll } = useDragAutoScroll();

  const handleGestureStop = useCallback(
    (currentLayout: Layout[]) => {
      stopAutoScroll();
      onInteractionStop(currentLayout);
    },
    [stopAutoScroll, onInteractionStop]
  );
  const { features, loading: featuresLoading, error: featuresError } = useFeatures();

  // Measure the grid container ourselves (react-grid-layout's WidthProvider only
  // re-measures on window resize, so it locked a stale/narrow width — causing all
  // tiles to pile up overlapping in the left strip). This width-only observer
  // tracks the real width through sidebar toggles, async content, and window
  // changes — and, crucially, re-renders ONLY when the integer width changes, not
  // when the grid's HEIGHT grows/shrinks as tiles are dragged or resized (which
  // previously fired a re-render on every gesture tick).
  const [gridWidth, setGridWidth] = useState(0);
  const gridObserverRef = useRef<ResizeObserver | null>(null);
  const gridRef = useCallback((node: HTMLDivElement | null) => {
    gridObserverRef.current?.disconnect();
    if (!node) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      setGridWidth(prev => (prev === width ? prev : width));
    });
    observer.observe(node);
    gridObserverRef.current = observer;
  }, []);

  // The app does not dark-theme antd via algorithm, so we scope a ConfigProvider
  // to this feature's antd components (toolbar, drawer, etc.) keyed on the active
  // HorizonTheme, keeping them correct in both light and dark mode.
  const horizonTheme = useTheme();
  const isDark = horizonTheme.themeName === Theme.Dark;
  // Memoized: a new theme object is a context change for every antd component on the
  // board (widget bodies included), re-rendering them and their CSS-in-JS on each render.
  const antdThemeConfig = useMemo(
    () => ({
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: { colorPrimary: horizonTheme.teal },
    }),
    [isDark, horizonTheme.teal]
  );

  const featureAccess = useMemo(
    () => ({ features, loading: featuresLoading, error: featuresError }),
    [features, featuresLoading, featuresError]
  );

  // Entitlement is a render-time filter, never a layout mutation. Some widgets retain
  // an unavailable preview so the existing placement can show its upgrade path.
  // Deleting any gated widget from the saved layout would make a transient denial permanent.
  const visibleInstances = useMemo(
    () =>
      layout.instances.filter(instance => {
        const definition = WIDGET_REGISTRY[instance.widgetId];
        return (
          definition &&
          (getWidgetAvailability(definition, featureAccess) !== 'unavailable' ||
            definition.renderWhenUnavailable)
        );
      }),
    [layout.instances, featureAccess]
  );

  const isEmpty = visibleInstances.length === 0;

  // Widgets are unique on the board, so the catalog only offers ones not yet placed.
  const placedWidgetIds = useMemo(
    () => new Set(layout.instances.map(instance => instance.widgetId)),
    [layout.instances]
  );
  const availableWidgetIds = useMemo(() => getAvailableWidgetIds(featureAccess), [featureAccess]);
  const addableWidgetCount = availableWidgetIds.filter(id => !placedWidgetIds.has(id)).length;
  const widgetAvailabilityById = useMemo(
    () =>
      ALL_WIDGET_IDS.reduce(
        (acc, id) => {
          acc[id] = getWidgetAvailability(WIDGET_REGISTRY[id], featureAccess);
          return acc;
        },
        {} as Record<WidgetId, WidgetAvailability>
      ),
    [featureAccess]
  );

  // Anything placed whose entitlement has not resolved stays listed too — otherwise it
  // would sit on the board with no way to remove it until the read settles.
  const listedWidgetIds = useMemo(
    () =>
      ALL_WIDGET_IDS.filter(id => {
        const definition = WIDGET_REGISTRY[id];
        const availability = widgetAvailabilityById[id];
        return (
          availability === 'available' ||
          (availability === 'pending' && placedWidgetIds.has(id)) ||
          (availability === 'unavailable' && definition.showWhenUnavailable)
        );
      }),
    [placedWidgetIds, widgetAvailabilityById]
  );

  // Memoize each widget's body element keyed on the instance list. Drag/resize only
  // mutate layout geometry (not `instances`), so these element references stay
  // stable across those changes — React then skips re-rendering the heavy widget
  // subtrees (charts, fleet map) on every layout event, which is the bulk of the
  // edit-mode lag with many tiles placed.
  const widgetBodies = useMemo(() => {
    const bodies: Record<string, ReactNode> = {};
    for (const instance of visibleInstances) {
      const definition = WIDGET_REGISTRY[instance.widgetId];
      if (!definition) {
        continue;
      }
      // Check access before loading the lazy component to prevent its queries from firing.
      if (getWidgetAvailability(definition, featureAccess) === 'pending') {
        bodies[instance.instanceId] = <Spinner />;
        continue;
      }
      const WidgetComponent = definition.component;
      bodies[instance.instanceId] = <WidgetComponent config={instance.config} />;
    }
    return bodies;
  }, [visibleInstances, featureAccess]);

  // react-grid-layout re-renders its tiles only when `children` changes identity, so
  // renders that don't touch the board (save status, catalog) skip them.
  const gridItems = useMemo(
    () =>
      visibleInstances.map(instance => {
        const definition = WIDGET_REGISTRY[instance.widgetId];
        if (!definition) {
          return null;
        }
        const title = instance.config?.titleOverride ?? definition.title;
        const layoutItem = layout.layouts.lg?.find(item => item.i === instance.instanceId);
        return (
          <div key={instance.instanceId}>
            <WidgetFrame
              instanceId={instance.instanceId}
              widgetId={instance.widgetId}
              title={title}
              dataState={definition.dataState}
              isEditing={isEditing}
              selfChrome={definition.selfChrome ?? false}
              currentSize={getWidgetSizePresetId(layoutItem)}
              configOptions={definition.configOptions}
              config={instance.config}
              onRemove={() => removeWidget(instance.instanceId)}
              onSizeChange={sizeId => setWidgetSize(instance.instanceId, sizeId)}
              onConfigChange={cfg => updateWidgetConfig(instance.instanceId, cfg)}
            >
              {widgetBodies[instance.instanceId]}
            </WidgetFrame>
          </div>
        );
      }),
    [
      visibleInstances,
      layout.layouts.lg,
      isEditing,
      widgetBodies,
      removeWidget,
      setWidgetSize,
      updateWidgetConfig,
    ]
  );

  return (
    <PageHeaderLayout title={t('navigation.dashboard', 'Dashboard')}>
      <ConfigProvider theme={antdThemeConfig}>
        <DashboardToolbar
          isEditing={isEditing}
          saveStatus={saveStatus}
          addableWidgetCount={addableWidgetCount}
          disabled={!isLoaded}
          onToggleEdit={setIsEditing}
          onAddClick={() => setCatalogOpen(true)}
        />

        {!isLoaded ? (
          <DashboardSkeleton />
        ) : isEmpty ? (
          <Empty description={t('components.dashboardEmpty', 'Your dashboard is empty.')}>
            <Button
              type="primary"
              onClick={() => {
                setIsEditing(true);
                setCatalogOpen(true);
              }}
            >
              {t('components.addAWidget', 'Add a widget')}
            </Button>
          </Empty>
        ) : (
          <DashboardGrid ref={gridRef} $isEditing={isEditing}>
            {gridWidth > 0 && (
              <Responsive
                className="layout"
                width={gridWidth}
                layouts={layout.layouts as Layouts}
                breakpoints={BREAKPOINTS}
                cols={COLS}
                rowHeight={ROW_HEIGHT}
                margin={MARGIN}
                isDraggable={isEditing}
                isResizable={isEditing}
                draggableHandle=".widget-drag-handle"
                draggableCancel=".widget-no-drag"
                onLayoutChange={onLayoutChange}
                onDragStart={onInteractionStart}
                onResizeStart={onInteractionStart}
                onDrag={updateAutoScroll}
                onResize={updateAutoScroll}
                onDragStop={handleGestureStop}
                onResizeStop={handleGestureStop}
                // Upward gravity: tiles in the dragged tile's way are pushed down, and
                // everything floats back up to fill gaps once the way is clear.
                compactType="vertical"
                preventCollision={false}
              >
                {gridItems}
              </Responsive>
            )}
          </DashboardGrid>
        )}

        <WidgetCatalogDrawer
          open={catalogOpen}
          listedWidgetIds={listedWidgetIds}
          placedWidgetIds={placedWidgetIds}
          widgetAvailabilityById={widgetAvailabilityById}
          onClose={() => setCatalogOpen(false)}
          // Keep the drawer open on add/remove so several widgets can be managed at once.
          onAdd={addWidget}
          onRemove={widgetId => {
            const instance = layout.instances.find(inst => inst.widgetId === widgetId);
            if (instance) {
              removeWidget(instance.instanceId);
            }
          }}
        />
      </ConfigProvider>
    </PageHeaderLayout>
  );
}
